import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  FormControl,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Select,
  SelectChangeEvent,
  Snackbar,
  SnackbarCloseReason,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  alpha
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';

import {
  LinhasEsqueletoTabela,
  ListagemBuscaField,
  paperListagemSx,
  tableContainerListagemSx,
} from '../../../shared/components';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { useDebounce, useListagemAltura, useListagemKeyboardNav } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import { EmpresaAdminService, IEmpresaListagem, IResumoEmpresas, TStatusPagamentoEmpresa } from '../../../shared/services/api/controle/EmpresaAdminService';
import { brand } from '../../../shared/themes';

const OPCOES_POR_PAGINA = [10, 25, 50, 100];

export const ListagemDeEmpresas: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { debounce } = useDebounce();
  const navigate = useNavigate();
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));

  const [rows, setRows] = useState<IEmpresaListagem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [resumo, setResumo] = useState<IResumoEmpresas | null>(null);
  const [openMsg, setOpenMsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const ROWS_CACHE_KEY = 'empresas';

  const { tabelaRef, containerHeight, rowsPerPage, setRowsPerPage } = useListagemAltura({
    cacheKey: ROWS_CACHE_KEY,
    valorInicial: Environment.LIMITE_DE_LINHAS,
    footerMode: 'medido',
    bottomGap: 16,
    recalcDep: totalCount,
  });

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(event.target.value));
  };

  const handleCloseMsg = (event?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') return;
    setOpenMsg(false);
  };

  const filter = useMemo(() => searchParams.get('filter') || '', [searchParams]);
  const pagina = useMemo(() => Number(searchParams.get('pagina') || '1'), [searchParams]);
  const statuspagamento = useMemo(() => (searchParams.get('statuspagamento') as TStatusPagamentoEmpresa | null) || '', [searchParams]);

  useEffect(() => {
    setIsLoading(true);

    debounce(() => {
      EmpresaAdminService.getAll(pagina, filter, rowsPerPage, statuspagamento || undefined)
        .then((result) => {
          setIsLoading(false);

          if (result instanceof Error) {
            setMsgType('error');
            setMsg(result.message);
            setOpenMsg(true);
          } else {
            setTotalCount(result.totalCount);
            setRows(result.data);
          }
        })
        .catch(() => {
          setIsLoading(false);
          setMsgType('error');
          setMsg('Erro ao carregar dados. Tente novamente mais tarde.');
          setOpenMsg(true);
        });
    });
  }, [pagina, filter, rowsPerPage, statuspagamento, debounce]);

  useEffect(() => {
    EmpresaAdminService.resumo(filter).then((result) => {
      if (!(result instanceof Error)) setResumo(result);
    });
  }, [filter, totalCount]);

  useEffect(() => {
    if (pagina !== 1) {
      setSearchParams({ filter, pagina: '1' }, { replace: true });
    }
  }, [rowsPerPage]);

  const primeiroItem = totalCount === 0 ? 0 : (pagina - 1) * rowsPerPage + 1;
  const ultimoItem = Math.min(pagina * rowsPerPage, totalCount);
  const totalPaginas = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  const { pesquisaRef, linhaRefCallback, onBuscaKeyDown, onLinhaKeyDown } = useListagemKeyboardNav({
    rowsLength: rows.length,
    pagina,
    totalPaginas,
    onIrParaPagina: (novaPagina) => setSearchParams({ filter, statuspagamento, pagina: novaPagina.toString() }, { replace: true }),
    onEnter: (index) => navigate(`/empresas/${rows[index].idempresa}`),
    rowsDep: [rows],
  });

  return (
    <LayoutBaseDePagina
      titulo='Empresas'
      subtitulo='Painel de controle das empresas clientes do SIAC.'
      acao={
        <Button
          variant='contained'
          disableElevation
          startIcon={<AddCircleOutlineRoundedIcon />}
          onClick={() => navigate('/empresas/novo')}
          sx={{
            height: 44,
            borderRadius: '10px',
            px: 2.5,
            fontWeight: 600,
            bgcolor: brand.menuBase,
            color: '#fff',
            '&:hover': { bgcolor: '#012D3D' },
          }}
        >
          Nova Empresa
        </Button>
      }
      barraDeFerramentas={
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mx: 1, mb: 2.5 }}>
          <ListagemBuscaField
            value={filter}
            onChange={(valor) => setSearchParams({ filter: valor, statuspagamento, pagina: '1' }, { replace: true })}
            placeholder='Pesquisar por nome, fantasia ou CNPJ...'
            inputRef={pesquisaRef}
            onKeyDown={onBuscaKeyDown}
          />

          <TextField
            select
            size='small'
            label='Pagamento'
            value={statuspagamento}
            onChange={(e) => setSearchParams({ filter, statuspagamento: e.target.value, pagina: '1' }, { replace: true })}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value=''>Todas</MenuItem>
            <MenuItem value='pagante'>Pagantes</MenuItem>
            <MenuItem value='naopaga'>Não pagam ainda</MenuItem>
          </TextField>

          <Box sx={{ display: 'flex', gap: 1, ml: { sm: 'auto' } }}>
            <Chip label={`Total: ${resumo?.total ?? '–'}`} size='small' sx={{ fontWeight: 600 }} />
            <Chip
              label={`Pagantes: ${resumo?.pagantes ?? '–'}`}
              size='small'
              sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.success.main, 0.12), color: 'success.main' }}
            />
            <Chip
              label={`Não pagam: ${resumo?.naopagam ?? '–'}`}
              size='small'
              sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.warning.main, 0.12), color: 'warning.main' }}
            />
          </Box>
        </Box>
      }
    >
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openMsg}
        autoHideDuration={msgType === 'error' ? null : 6000}
        onClose={handleCloseMsg}
      >
        <Alert onClose={handleCloseMsg} severity={msgType} variant='filled' sx={{ width: '100%' }}>
          {msg}
        </Alert>
      </Snackbar>

      <Paper elevation={0} sx={paperListagemSx(theme)}>
        <TableContainer
          ref={tabelaRef}
          sx={tableContainerListagemSx(containerHeight, isLoading || rows.length >= rowsPerPage)}
        >
          <Table size="small" sx={{ width: '100%', minWidth: smDown ? 0 : 720, tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '48px' }} />
                <TableCell sx={{ width: '24%' }}>Empresa</TableCell>
                <TableCell sx={{ width: '14%' }}>CNPJ</TableCell>
                <TableCell sx={{ width: '13%' }}>Telefone</TableCell>
                <TableCell align='center' sx={{ width: '30%' }}>Mensalidade</TableCell>
                <TableCell align='center' sx={{ width: '14%' }}>Status</TableCell>
              </TableRow>
            </TableHead>

            {isLoading && rows.length === 0 && (
              <TableBody>
                <LinhasEsqueletoTabela
                  colunas={[
                    { align: 'center', variant: 'circular', width: 32, height: 32 },
                    { width: '75%' },
                    { width: '60%' },
                    { width: '50%' },
                    { align: 'center', variant: 'rounded', width: 130, height: 22 },
                    { align: 'center', variant: 'rounded', width: 60, height: 22 },
                  ]}
                />
              </TableBody>
            )}
            <TableBody>
              {rows.map((row, index) => (
                <TableRow
                  key={row.idempresa}
                  ref={linhaRefCallback(index)}
                  hover
                  tabIndex={0}
                  onClick={() => navigate(`/empresas/${row.idempresa}`)}
                  onKeyDown={onLinhaKeyDown(index)}
                  sx={{
                    cursor: 'pointer',
                    outline: 'none',
                    '&:last-child td': { borderBottom: 0 },
                    '& td': {
                      py: smDown ? 0.75 : 1.25,
                      px: smDown ? 0.75 : 1.5,
                      fontSize: smDown ? '0.75rem' : '0.8125rem',
                      borderBottom: `1px solid ${theme.torq.border.default}`,
                    },
                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.035) },
                    '&:focus-visible': {
                      outline: `2px solid ${theme.torq.border.focus}`,
                      outlineOffset: '-2px',
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                >
                  <TableCell>
                    <Avatar
                      variant='rounded'
                      sx={{ width: 32, height: 32, margin: 'auto', fontSize: '0.8rem' }}
                      alt={row.fantasia || row.nome}
                      src={row.logo ? Environment.URL_IMGLOG + row.logo : undefined}
                    >
                      <BusinessRoundedIcon fontSize='small' />
                    </Avatar>
                  </TableCell>

                  <TableCell>
                    <Typography fontWeight={600} fontSize="0.8125rem" color={theme.torq.text.primary} noWrap>
                      {row.nome}
                    </Typography>
                    {!!row.fantasia && (
                      <Typography fontSize="0.7rem" color={theme.torq.text.secondary} noWrap>
                        {row.fantasia}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>
                      {row.cnpj}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>
                      {row.telefone || '-'}
                    </Typography>
                  </TableCell>

                  <TableCell align='center'>
                    <Box display='flex' flexDirection='column' alignItems='center' gap={0.5}>
                      <Chip
                        label={(row.totalparcelas || 0) > 0 ? 'Parcelas geradas' : 'Sem parcelas'}
                        size="small"
                        sx={{
                          height: '20px',
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          '& .MuiChip-label': { px: 1 },
                          bgcolor: (row.totalparcelas || 0) > 0 ? alpha(theme.palette.info.main, 0.12) : alpha(theme.palette.text.secondary, 0.12),
                          color: (row.totalparcelas || 0) > 0 ? 'info.main' : 'text.secondary',
                        }}
                      />
                      <Chip
                        label={(row.totalpagas || 0) > 0 ? 'Pagando' : 'Não paga ainda'}
                        size="small"
                        sx={{
                          height: '20px',
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          '& .MuiChip-label': { px: 1 },
                          bgcolor: (row.totalpagas || 0) > 0 ? alpha(theme.palette.success.main, 0.12) : alpha(theme.palette.warning.main, 0.12),
                          color: (row.totalpagas || 0) > 0 ? 'success.main' : 'warning.main',
                        }}
                      />
                    </Box>
                  </TableCell>

                  <TableCell align='center'>
                    <Chip
                      label={row.status ? 'Ativa' : 'Inativa'}
                      size="small"
                      sx={{
                        height: '22px',
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        '& .MuiChip-label': { px: 1.25 },
                        bgcolor: row.status ? alpha(theme.palette.success.main, 0.12) : alpha(theme.palette.error.main, 0.12),
                        color: row.status ? 'success.main' : 'error.main',
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>

            {totalCount === 0 && !isLoading && (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={6} align='center' sx={{ py: 3 }}>
                    <Box>
                      <BusinessRoundedIcon sx={{
                        fontSize: 35,
                        color: theme.palette.mode === 'dark' ? alpha(theme.palette.text.secondary, 0.4) : 'text.secondary',
                        mb: 1
                      }} />
                      <Typography variant='h6' color='text.secondary' gutterBottom fontSize="0.85rem">
                        {Environment.LISTAGEM_VAZIA}
                      </Typography>
                      <Typography variant='body2' color='text.secondary' fontSize="0.7rem">
                        {filter ? 'Tente ajustar os filtros de busca' : 'Clique em "Nova Empresa" para começar'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            )}

            <TableFooter>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ p: 0 }}>
                    <LinearProgress sx={{ height: '1.5px' }} />
                  </TableCell>
                </TableRow>
              )}

              <TableRow>
                <TableCell colSpan={6} sx={{ py: 1.25, px: 2, borderTop: `1px solid ${theme.torq.border.default}` }}>
                  <Box display='flex' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap={1.5}>
                    <Typography variant='caption' color='text.secondary'>
                      {totalCount === 0
                        ? 'Nenhuma empresa encontrada'
                        : `Mostrando de ${primeiroItem} a ${ultimoItem} de ${totalCount} empresa${totalCount !== 1 ? 's' : ''}`}
                    </Typography>

                    {totalCount > rowsPerPage && (
                      <Pagination
                        page={pagina}
                        count={totalPaginas}
                        onChange={(_, newPage) => setSearchParams({ filter, statuspagamento, pagina: newPage.toString() }, { replace: true })}
                        size="small"
                        shape="rounded"
                        showFirstButton
                        showLastButton
                        siblingCount={1}
                        boundaryCount={1}
                        sx={{
                          '& .MuiPaginationItem-root.Mui-selected': {
                            backgroundColor: brand.menuBase,
                            color: '#fff',
                            '&:hover': { backgroundColor: '#012D3D' },
                          },
                        }}
                      />
                    )}

                    <FormControl size='small' sx={{ minWidth: 110 }}>
                      <Select<number> value={rowsPerPage} onChange={handleChangeRowsPerPage}>
                        {(OPCOES_POR_PAGINA.includes(rowsPerPage) ? OPCOES_POR_PAGINA : [...OPCOES_POR_PAGINA, rowsPerPage].sort((a, b) => a - b)).map((opcao) => (
                          <MenuItem key={opcao} value={opcao}>{opcao} / página</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>
    </LayoutBaseDePagina>
  );
};
